import React, { useEffect, useState } from 'react';
import {
  calculateRevenueAccrual,
  postRevenueAccrual,
  getAccrualHistory,
  createDeferredRevenue,
  recognizeDeferredRevenue,
  getDeferredRevenue,
  recordCost,
  getCosts,
  getCostSummary,
  getContractMargin,
  getMarginSummary,
  getCompanies,
  getContracts
} from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Calculator,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Calendar,
  Building2,
  Receipt,
  Clock,
  Plus,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  PieChart,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  FileText,
  Fuel,
  Wrench,
  Users,
  Truck
} from 'lucide-react';
import { toast } from 'sonner';

const costTypeConfig = {
  supplier_rental: { label: 'Supplier Rental', icon: Truck, color: 'bg-blue-100 text-blue-800' },
  driver: { label: 'Driver', icon: Users, color: 'bg-purple-100 text-purple-800' },
  fuel: { label: 'Fuel', icon: Fuel, color: 'bg-amber-100 text-amber-800' },
  maintenance: { label: 'Maintenance', icon: Wrench, color: 'bg-red-100 text-red-800' },
  insurance: { label: 'Insurance', icon: FileText, color: 'bg-green-100 text-green-800' },
  admin: { label: 'Admin', icon: Building2, color: 'bg-gray-100 text-gray-800' },
  other: { label: 'Other', icon: DollarSign, color: 'bg-slate-100 text-slate-800' }
};

export const RevenueCostPage = () => {
  const [companies, setCompanies] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);

  // Period selection
  const [selectedPeriod, setSelectedPeriod] = useState(
    new Date().toISOString().slice(0, 7)
  );

  // Accrual state
  const [accrualResult, setAccrualResult] = useState(null);
  const [accrualHistory, setAccrualHistory] = useState([]);

  // Deferred Revenue state
  const [deferredEntries, setDeferredEntries] = useState([]);
  const [deferredDialogOpen, setDeferredDialogOpen] = useState(false);
  const [newDeferred, setNewDeferred] = useState({
    contract_id: '',
    payment_amount: 0,
    payment_date: new Date().toISOString().split('T')[0],
    recognition_periods: 12,
    start_period: new Date().toISOString().slice(0, 7)
  });

  // Cost state
  const [costs, setCosts] = useState([]);
  const [costSummary, setCostSummary] = useState(null);
  const [costDialogOpen, setCostDialogOpen] = useState(false);
  const [newCost, setNewCost] = useState({
    cost_type: 'supplier_rental',
    amount: 0,
    cost_date: new Date().toISOString().split('T')[0],
    description: '',
    reference_number: '',
    is_billable: true,
    contract_id: '',
    asset_id: ''
  });

  // Margin state
  const [marginSummary, setMarginSummary] = useState(null);
  const [selectedContractMargin, setSelectedContractMargin] = useState(null);

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      loadContracts();
      loadCosts();
      loadCostSummary();
      loadDeferredRevenue();
      loadMarginSummary();
    }
  }, [selectedCompany, selectedPeriod]);

  const loadCompanies = async () => {
    try {
      const response = await getCompanies();
      setCompanies(response.data);
      if (response.data.length > 0) {
        setSelectedCompany(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  const loadContracts = async () => {
    try {
      const response = await getContracts(selectedCompany);
      setContracts(response.data);
    } catch (error) {
      console.error('Failed to load contracts:', error);
    }
  };

  const loadCosts = async () => {
    try {
      const response = await getCosts(selectedCompany, { period: selectedPeriod });
      setCosts(response.data);
    } catch (error) {
      console.error('Failed to load costs:', error);
    }
  };

  const loadCostSummary = async () => {
    try {
      const response = await getCostSummary(selectedCompany, null, selectedPeriod);
      setCostSummary(response.data);
    } catch (error) {
      console.error('Failed to load cost summary:', error);
    }
  };

  const loadDeferredRevenue = async () => {
    try {
      const response = await getDeferredRevenue(selectedCompany);
      setDeferredEntries(response.data);
    } catch (error) {
      console.error('Failed to load deferred revenue:', error);
    }
  };

  const loadMarginSummary = async () => {
    try {
      const response = await getMarginSummary(selectedCompany);
      setMarginSummary(response.data);
    } catch (error) {
      console.error('Failed to load margin summary:', error);
    }
  };

  const handleCalculateAccrual = async () => {
    setLoading(true);
    try {
      const response = await calculateRevenueAccrual({
        company_id: selectedCompany,
        period_date: selectedPeriod
      });
      setAccrualResult(response.data);
      toast.success(`Accrual calculated: ${response.data.total_accrued?.toLocaleString()} SAR`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to calculate accrual');
    } finally {
      setLoading(false);
    }
  };

  const handlePostAccrual = async () => {
    try {
      const response = await postRevenueAccrual({
        company_id: selectedCompany,
        period_date: selectedPeriod
      });
      toast.success(`Accrual posted: ${response.data.journal_number}`);
      setAccrualResult(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post accrual');
    }
  };

  const handleRecordCost = async () => {
    try {
      await recordCost({
        company_id: selectedCompany,
        ...newCost
      });
      toast.success('Cost recorded successfully');
      setCostDialogOpen(false);
      loadCosts();
      loadCostSummary();
      setNewCost({
        cost_type: 'supplier_rental',
        amount: 0,
        cost_date: new Date().toISOString().split('T')[0],
        description: '',
        reference_number: '',
        is_billable: true,
        contract_id: '',
        asset_id: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record cost');
    }
  };

  const handleCreateDeferred = async () => {
    try {
      await createDeferredRevenue({
        company_id: selectedCompany,
        ...newDeferred
      });
      toast.success('Deferred revenue entry created');
      setDeferredDialogOpen(false);
      loadDeferredRevenue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create deferred revenue');
    }
  };

  const handleRecognizeDeferred = async () => {
    try {
      const response = await recognizeDeferredRevenue({
        company_id: selectedCompany,
        period_date: selectedPeriod
      });
      toast.success(`Recognized: ${response.data.total_recognized?.toLocaleString()} SAR`);
      loadDeferredRevenue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nothing to recognize for this period');
    }
  };

  // Summary calculations
  const totalCosts = costSummary?.total_costs || 0;
  const totalBillable = costSummary?.total_billable || 0;
  const overallMargin = marginSummary?.summary?.overall_margin || 0;
  const overallMarginPct = marginSummary?.summary?.overall_margin_pct || 0;
  const atRiskCount = marginSummary?.at_risk_contracts?.length || 0;
  const totalDeferredRemaining = deferredEntries.reduce((sum, d) => sum + (d.remaining_amount || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight" data-testid="revenue-cost-title">
            Revenue & Cost Control
          </h1>
          <p className="text-muted-foreground mt-1">
            Accruals, Deferred Revenue, Cost Tracking & Margin Analysis
          </p>
        </div>
        <div className="flex gap-2">
          <Input
            type="month"
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="w-40"
          />
          <Select value={selectedCompany} onValueChange={setSelectedCompany}>
            <SelectTrigger className="w-[200px]">
              <Building2 className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Select Company" />
            </SelectTrigger>
            <SelectContent>
              {companies.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="h-5 w-5 text-blue-600" />
            <span className="text-sm text-muted-foreground">Total Costs</span>
          </div>
          <p className="text-2xl font-bold">{totalCosts.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">SAR this period</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Receipt className="h-5 w-5 text-green-600" />
            <span className="text-sm text-muted-foreground">Billable Costs</span>
          </div>
          <p className="text-2xl font-bold text-green-600">{totalBillable.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">Recoverable from clients</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-5 w-5 text-purple-600" />
            <span className="text-sm text-muted-foreground">Overall Margin</span>
          </div>
          <p className={`text-2xl font-bold ${overallMarginPct >= 20 ? 'text-green-600' : overallMarginPct >= 10 ? 'text-amber-600' : 'text-red-600'}`}>
            {overallMarginPct.toFixed(1)}%
          </p>
          <p className="text-xs text-muted-foreground">{overallMargin.toLocaleString()} SAR</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="h-5 w-5 text-amber-600" />
            <span className="text-sm text-muted-foreground">Deferred Revenue</span>
          </div>
          <p className="text-2xl font-bold">{totalDeferredRemaining.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">Remaining to recognize</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <span className="text-sm text-muted-foreground">At Risk</span>
          </div>
          <p className="text-2xl font-bold text-red-600">{atRiskCount}</p>
          <p className="text-xs text-muted-foreground">Contracts {"<15%"} margin</p>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview" data-testid="overview-tab">Overview</TabsTrigger>
          <TabsTrigger value="costs" data-testid="costs-tab">Cost Tracking</TabsTrigger>
          <TabsTrigger value="accruals" data-testid="accruals-tab">Revenue Accrual</TabsTrigger>
          <TabsTrigger value="deferred" data-testid="deferred-tab">Deferred Revenue</TabsTrigger>
          <TabsTrigger value="margins" data-testid="margins-tab">Margin Analysis</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Cost Breakdown */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <PieChart className="h-5 w-5" />
                Cost Breakdown - {selectedPeriod}
              </h3>
              <div className="space-y-3">
                {costSummary?.by_type?.map(item => {
                  const config = costTypeConfig[item.type] || costTypeConfig.other;
                  const Icon = config.icon;
                  const percentage = totalCosts > 0 ? (item.total / totalCosts * 100).toFixed(1) : 0;
                  
                  return (
                    <div key={item.type} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${config.color}`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="font-medium">{config.label}</p>
                          <p className="text-xs text-muted-foreground">{item.count} entries</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">{item.total.toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground">{percentage}%</p>
                      </div>
                    </div>
                  );
                })}
                {(!costSummary?.by_type || costSummary.by_type.length === 0) && (
                  <p className="text-center text-muted-foreground py-8">No costs recorded for this period</p>
                )}
              </div>
            </Card>

            {/* At Risk Contracts */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                At-Risk Contracts ({"<15%"} Margin)
              </h3>
              <div className="space-y-3">
                {marginSummary?.at_risk_contracts?.slice(0, 5).map(contract => (
                  <div key={contract.contract_id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                    <div>
                      <p className="font-medium">{contract.contract_number || contract.contract_id}</p>
                      <p className="text-sm text-muted-foreground">{contract.client_name}</p>
                    </div>
                    <div className="text-right">
                      <Badge className="bg-red-100 text-red-800">
                        {contract.margin_pct.toFixed(1)}% margin
                      </Badge>
                      <p className="text-xs text-muted-foreground mt-1">
                        Costs: {contract.costs?.toLocaleString()} SAR
                      </p>
                    </div>
                  </div>
                ))}
                {(!marginSummary?.at_risk_contracts || marginSummary.at_risk_contracts.length === 0) && (
                  <div className="text-center py-8">
                    <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-2" />
                    <p className="text-muted-foreground">All contracts have healthy margins</p>
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Quick Actions */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => setCostDialogOpen(true)} data-testid="record-cost-btn">
                <Plus className="h-4 w-4 mr-2" />
                Record Cost
              </Button>
              <Button variant="outline" onClick={handleCalculateAccrual}>
                <Calculator className="h-4 w-4 mr-2" />
                Calculate Revenue Accrual
              </Button>
              <Button variant="outline" onClick={handleRecognizeDeferred}>
                <Clock className="h-4 w-4 mr-2" />
                Recognize Deferred Revenue
              </Button>
              <Button variant="outline" onClick={() => {
                loadCosts();
                loadCostSummary();
                loadMarginSummary();
                toast.success('Data refreshed');
              }}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </Card>
        </TabsContent>

        {/* Cost Tracking Tab */}
        <TabsContent value="costs" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Cost Entries - {selectedPeriod}
            </h2>
            <Button onClick={() => setCostDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Record Cost
            </Button>
          </div>

          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Type</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Description</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Reference</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">Amount</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Billable</th>
                  </tr>
                </thead>
                <tbody>
                  {costs.map(cost => {
                    const config = costTypeConfig[cost.cost_type] || costTypeConfig.other;
                    return (
                      <tr key={cost.id} className="border-b hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm">{cost.cost_date}</td>
                        <td className="px-4 py-3">
                          <Badge className={config.color}>{config.label}</Badge>
                        </td>
                        <td className="px-4 py-3 text-sm">{cost.description || '-'}</td>
                        <td className="px-4 py-3 text-sm font-mono">{cost.reference_number || '-'}</td>
                        <td className="px-4 py-3 text-right font-mono font-semibold">
                          {cost.amount.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {cost.is_billable ? (
                            <CheckCircle2 className="h-4 w-4 text-green-600 mx-auto" />
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {costs.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">
                        <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No costs recorded for this period</p>
                        <Button className="mt-4" onClick={() => setCostDialogOpen(true)}>
                          <Plus className="h-4 w-4 mr-2" />
                          Record First Cost
                        </Button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* Revenue Accrual Tab */}
        <TabsContent value="accruals" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              Revenue Accrual
            </h2>
          </div>

          <Card className="p-4">
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <Label>Period</Label>
                <Input
                  type="month"
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className="mt-1"
                />
              </div>
              <Button onClick={handleCalculateAccrual} disabled={loading}>
                <Calculator className="h-4 w-4 mr-2" />
                Calculate Accrual
              </Button>
              {accrualResult?.entries?.length > 0 && (
                <Button onClick={handlePostAccrual} className="bg-green-600">
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Post to GL
                </Button>
              )}
            </div>
          </Card>

          {accrualResult && (
            <Card>
              <div className="p-4 border-b bg-slate-50">
                <h3 className="font-semibold">Accrual for {accrualResult.period}</h3>
                <p className="text-sm text-muted-foreground">
                  Total: SAR {accrualResult.total_accrued?.toLocaleString()}
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Contract</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Client</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Days</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold">Monthly Rate</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold">Accrued Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accrualResult.entries?.map(entry => (
                      <tr key={entry.id} className="border-b">
                        <td className="px-4 py-3 text-sm font-mono">{entry.contract_number}</td>
                        <td className="px-4 py-3 text-sm">{entry.client_name}</td>
                        <td className="px-4 py-3 text-center">{entry.days_in_period}</td>
                        <td className="px-4 py-3 text-right font-mono">{entry.monthly_rate?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right font-mono font-semibold text-green-600">
                          {entry.accrued_amount?.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {!accrualResult && (
            <Card className="p-12 text-center">
              <Calculator className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Calculate Revenue Accrual</h3>
              <p className="text-muted-foreground">
                Select a period and calculate prorated revenue for all active contracts
              </p>
            </Card>
          )}
        </TabsContent>

        {/* Deferred Revenue Tab */}
        <TabsContent value="deferred" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Deferred Revenue
            </h2>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleRecognizeDeferred}>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Recognize for {selectedPeriod}
              </Button>
              <Button onClick={() => setDeferredDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Deferred Entry
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {deferredEntries.map(entry => (
              <Card key={entry.id} className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-semibold">{entry.contract_number || entry.contract_id}</h3>
                    <p className="text-sm text-muted-foreground">{entry.client_name}</p>
                  </div>
                  <Badge className={entry.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                    {entry.status}
                  </Badge>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Original Amount:</span>
                    <span className="font-mono">{entry.payment_amount?.toLocaleString()} SAR</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Recognized:</span>
                    <span className="font-mono text-green-600">{entry.recognized_amount?.toLocaleString()} SAR</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Remaining:</span>
                    <span className="font-mono font-semibold">{entry.remaining_amount?.toLocaleString()} SAR</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Monthly:</span>
                    <span className="font-mono">{entry.monthly_recognition?.toLocaleString()} SAR</span>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="mt-3">
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500 transition-all"
                      style={{ width: `${(entry.recognized_amount / entry.payment_amount) * 100}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 text-right">
                    {Math.round((entry.recognized_amount / entry.payment_amount) * 100)}% recognized
                  </p>
                </div>
              </Card>
            ))}
            
            {deferredEntries.length === 0 && (
              <Card className="p-12 text-center col-span-full">
                <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Deferred Revenue</h3>
                <p className="text-muted-foreground mb-4">
                  Create entries for advance payments that need to be recognized over time
                </p>
                <Button onClick={() => setDeferredDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Entry
                </Button>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Margin Analysis Tab */}
        <TabsContent value="margins" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Margin Analysis
            </h2>
            <Button variant="outline" onClick={loadMarginSummary}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>

          {marginSummary && (
            <>
              <div className="grid grid-cols-4 gap-4">
                <Card className="p-4">
                  <p className="text-sm text-muted-foreground mb-1">Quoted Revenue</p>
                  <p className="text-2xl font-bold">{marginSummary.summary?.total_quoted_revenue?.toLocaleString()}</p>
                </Card>
                <Card className="p-4">
                  <p className="text-sm text-muted-foreground mb-1">Invoiced Revenue</p>
                  <p className="text-2xl font-bold text-green-600">{marginSummary.summary?.total_invoiced_revenue?.toLocaleString()}</p>
                </Card>
                <Card className="p-4">
                  <p className="text-sm text-muted-foreground mb-1">Total Costs</p>
                  <p className="text-2xl font-bold text-red-600">{marginSummary.summary?.total_costs?.toLocaleString()}</p>
                </Card>
                <Card className="p-4">
                  <p className="text-sm text-muted-foreground mb-1">Overall Margin</p>
                  <p className={`text-2xl font-bold ${marginSummary.summary?.overall_margin_pct >= 20 ? 'text-green-600' : 'text-amber-600'}`}>
                    {marginSummary.summary?.overall_margin_pct?.toFixed(1)}%
                  </p>
                </Card>
              </div>

              <Card>
                <div className="p-4 border-b bg-slate-50">
                  <h3 className="font-semibold">Contract Margins (Sorted by Lowest)</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Contract</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Client</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold">Quoted</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold">Invoiced</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold">Costs</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold">Margin</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {marginSummary.contracts?.map(contract => (
                        <tr key={contract.contract_id} className="border-b hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-mono">{contract.contract_number || contract.contract_id}</td>
                          <td className="px-4 py-3 text-sm">{contract.client_name}</td>
                          <td className="px-4 py-3 text-right font-mono">{contract.quoted?.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right font-mono text-green-600">{contract.invoiced?.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right font-mono text-red-600">{contract.costs?.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right font-mono font-semibold">
                            {contract.margin?.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge className={
                              contract.margin_pct >= 20 ? 'bg-green-100 text-green-800' :
                              contract.margin_pct >= 10 ? 'bg-amber-100 text-amber-800' :
                              'bg-red-100 text-red-800'
                            }>
                              {contract.margin_pct?.toFixed(1)}%
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Record Cost Dialog */}
      <Dialog open={costDialogOpen} onOpenChange={setCostDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Record Cost
            </DialogTitle>
            <DialogDescription>
              Add a new cost entry for tracking
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label>Cost Type *</Label>
              <Select
                value={newCost.cost_type}
                onValueChange={(v) => setNewCost({...newCost, cost_type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(costTypeConfig).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount (SAR) *</Label>
                <Input
                  type="number"
                  value={newCost.amount || ''}
                  onChange={(e) => setNewCost({...newCost, amount: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={newCost.cost_date}
                  onChange={(e) => setNewCost({...newCost, cost_date: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={newCost.description}
                onChange={(e) => setNewCost({...newCost, description: e.target.value})}
                rows={2}
              />
            </div>

            <div>
              <Label>Reference Number</Label>
              <Input
                value={newCost.reference_number}
                onChange={(e) => setNewCost({...newCost, reference_number: e.target.value})}
                placeholder="Invoice/PO number"
              />
            </div>

            <div>
              <Label>Contract (Optional)</Label>
              <Select
                value={newCost.contract_id || "none"}
                onValueChange={(v) => setNewCost({...newCost, contract_id: v === "none" ? "" : v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select contract" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No contract</SelectItem>
                  {contracts.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.contract_number} - {c.client_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_billable"
                checked={newCost.is_billable}
                onChange={(e) => setNewCost({...newCost, is_billable: e.target.checked})}
                className="rounded"
              />
              <Label htmlFor="is_billable">Billable to client</Label>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setCostDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleRecordCost}
                disabled={!newCost.amount || !newCost.cost_date}
                className="bg-accent"
              >
                Record Cost
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Deferred Revenue Dialog */}
      <Dialog open={deferredDialogOpen} onOpenChange={setDeferredDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Create Deferred Revenue
            </DialogTitle>
            <DialogDescription>
              Track advance payments to be recognized over time
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label>Contract *</Label>
              <Select
                value={newDeferred.contract_id}
                onValueChange={(v) => setNewDeferred({...newDeferred, contract_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select contract" />
                </SelectTrigger>
                <SelectContent>
                  {contracts.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.contract_number} - {c.client_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Payment Amount *</Label>
                <Input
                  type="number"
                  value={newDeferred.payment_amount || ''}
                  onChange={(e) => setNewDeferred({...newDeferred, payment_amount: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Payment Date *</Label>
                <Input
                  type="date"
                  value={newDeferred.payment_date}
                  onChange={(e) => setNewDeferred({...newDeferred, payment_date: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Recognition Periods (months)</Label>
                <Input
                  type="number"
                  value={newDeferred.recognition_periods || ''}
                  onChange={(e) => setNewDeferred({...newDeferred, recognition_periods: parseInt(e.target.value) || 12})}
                />
              </div>
              <div>
                <Label>Start Period</Label>
                <Input
                  type="month"
                  value={newDeferred.start_period}
                  onChange={(e) => setNewDeferred({...newDeferred, start_period: e.target.value})}
                />
              </div>
            </div>

            {newDeferred.payment_amount > 0 && newDeferred.recognition_periods > 0 && (
              <Card className="p-3 bg-slate-50">
                <p className="text-sm font-semibold mb-2">Recognition Preview:</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Monthly Amount:</span>
                    <p className="font-mono font-semibold">
                      {(newDeferred.payment_amount / newDeferred.recognition_periods).toLocaleString(undefined, {maximumFractionDigits: 2})} SAR
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Recognition Period:</span>
                    <p className="font-mono font-semibold">
                      {newDeferred.recognition_periods} months
                    </p>
                  </div>
                </div>
              </Card>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setDeferredDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleCreateDeferred}
                disabled={!newDeferred.contract_id || !newDeferred.payment_amount}
                className="bg-accent"
              >
                Create Entry
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
